import { AssetScreenerService } from "../services/assetScreener.service"
import { type TokenDO } from "../../tokenData/models/tokenDO.models"
import '../styles/assetScreener.style.scss'
import { formatter } from "../../core/helpers/formatter"
import { AssetScreenerHeaderComponent } from "./assetScreenerHeader.component"
import { type WebflowToken } from "../models/assetScreener.models"
export class AssetScreenerComponent extends HTMLElement {
    private webflowTokens: WebflowToken[] = []
    private assetData: TokenDO[] = []
    private currentSort: { field: string | null, direction: 'asc' | 'desc' | null } = { field: 'null', direction: null }
    private currentSector: string | null = null

    constructor(private assetScreenerService: AssetScreenerService = window.tbwServices.assetScreenerService) {
        super()
        this.classList.add('asset-screener-container')
    }

    connectedCallback() {
        this.webflowTokens = this.fetchWebflowTokens()
        if (this.webflowTokens.length === 0) {
            // console.log('No slugs found')
            return
        }
        this.init()
        this.setupTooltipListeners()
    }

    async init() {
        this.assetData = await this.load()
        this.assetData = AssetScreenerHeaderComponent.sortData(
            this.assetData,
            { field: 'cmc_rank', direction: 'asc' }
        )
        this.currentSector = this.getCurrentSector()
        this.createScreener()
        this.style.display = 'flex'
    }

    getCurrentSector() {
        const sectorSlug = document.querySelector('[data-sector-slug]')?.getAttribute('data-sector-slug') ?? null
        return sectorSlug
    }

    async createScreener() {
        const table = document.createElement('table')
        this.createHeaderRow(table)
        this.createAssetRows(table)
        this.appendChild(table)
    }

    async createHeaderRow(table: HTMLTableElement) {
        const tHead = table.createTHead()
        const headerComponent = new AssetScreenerHeaderComponent((sort) => {
            this.currentSort = sort
            this.assetData = AssetScreenerHeaderComponent.sortData(this.assetData, sort)
            this.refreshScreener()
        }, this.currentSort)
        tHead.appendChild(headerComponent.getElement())
    }

    async createAssetRows(table: HTMLTableElement) {
        const tBody = table.createTBody()
        this.assetData.forEach((asset) => {
            const row = document.createElement('tr')

            // Puts the token icon in the first column
            const iconSrc = this.webflowTokens.find(token => token.slug === asset.slug)?.icon
            const iconHtml = iconSrc ? `<img src="${iconSrc}" alt="${asset.name}" class="token-icon">` : ''
            const currentSector = asset.sectors?.find(sector => sector.slug === this.currentSector) ?? asset.sectors?.[0]
            const otherSectors = asset.sectors?.filter(sector => sector.slug !== currentSector?.slug)


            row.innerHTML = `
            <td >
            <div class="is-link">
                <a class="caption1" href="/token-summary/${asset.slug}">
                    <div class="token-icon-container">
                        ${iconHtml} 
                 </div>
                ${asset.name}
                </a>
                <div class="is-link">
                    <a href="/token-summary/${asset.slug}" class="caption1">${asset.ticker}</a>
                </div>
            </div>
            </td>
            <td><p class="caption1">${formatter.formatPrice(asset.data.price) ?? ''}</p></td>
            <td style="color: ${this.getColor(asset.data.percent_change_24h)}"><p class="caption1">${formatter.formatPercentage(asset.data.percent_change_24h) ?? ''}</p></td>
            <td style="color: ${this.getColor(asset.data.percent_change_7d)}"><p class="caption1">${formatter.formatPercentage(asset.data.percent_change_7d) ?? ''}</p></td>
            <td style="color: ${this.getColor(asset.data.percent_change_30d)}"><p class="caption1">${formatter.formatPercentage(asset.data.percent_change_30d) ?? ''}</p></td>
            <td><p class="caption1">${formatter.formatLargeNumber(asset.data.market_cap) ?? ''}</p></td>
            <td><p class="caption1">${formatter.formatLargeNumber(asset.data.volume_24h) ?? ''}</p></td>
            <td><p class="caption1">${formatter.formatLargeNumber(asset.data.fully_diluted_market_cap) ?? ''}</p></td>
            <td>
            <div class="is-link">
                ${currentSector ? `<a href="/token-category/${currentSector.slug}"><p class="caption1">${currentSector.name}</p></a>` : ''}
                ${otherSectors?.length ? `
                    <div class="tooltip-container">
                        <button class="see-more-sectors caption1" data-tooltip-id="${asset.slug}">+${otherSectors.length}</button>
                        <div class="sector-tooltip" id="tooltip-${asset.slug}">
                            ${otherSectors?.map(sector => this.createSectorTooltip(sector)).join('')}
                        </div>
                    </div>
                ` : ''}
                </div>
            </td>
            `

            tBody.appendChild(row)
        })
    }

    createSectorTooltip(sector: { name: string, slug: string }) {
        return `<a class="caption1" href="/token-category/${sector.slug}">${sector.name}</a>`
    }

    getColor(value: number): string {
        return value < 0 ? 'var(--coral)' : 'var(--green-percentage)'
    }

    async refreshScreener() {
        this.innerHTML = ''
        this.createScreener()
    }

    async load(): Promise<TokenDO[]> {
        const slugsString = this.webflowTokens.map(slug => slug.slug).join(',')
        const screenerData = await this.assetScreenerService.getTokenData(slugsString)

        // Merge sector information from webflowTokens into screenerData
        return screenerData.map(asset => {
            const webflowToken = this.webflowTokens.find(token => token.slug === asset.slug)
            return {
                ...asset,
                sectors: webflowToken?.sectors || []
            }
        })
    }

    //@ts-ignore
    fetchWebflowTokens(): WebflowToken[] | any {
        const slugArray = document.querySelectorAll('[data-token-slug]') as NodeListOf<HTMLDivElement>
        const webflowTokens: WebflowToken[] = []
        slugArray.forEach((slug) => {
            const sectorName = slug.parentElement?.nextElementSibling?.querySelectorAll("[data-token-sector-name]")
            const sectors = Array.from(sectorName).map((name) => ({
                name: name.getAttribute('data-token-sector-name'),
                slug: name.getAttribute('data-token-sector-slug')
            }))
            webflowTokens.push({
                slug: slug.dataset.tokenSlug!,
                icon: slug.dataset.tokenIcon!,
                sectors
            })
        })

        return webflowTokens
    }

    private setupTooltipListeners() {
        // Handle clicking the +X button
        this.addEventListener('click', (event) => {
            const target = event.target as HTMLElement
            if (target.classList.contains('see-more-sectors')) {
                const tooltipId = target.getAttribute('data-tooltip-id')
                if (tooltipId) {
                    const tooltip = this.querySelector(`#tooltip-${tooltipId}`) as HTMLElement
                    if (tooltip) {
                        // Hide all other tooltips first
                        this.querySelectorAll('.sector-tooltip').forEach(t => {
                            if (t.id !== `tooltip-${tooltipId}`) {
                                (t as HTMLElement).style.display = 'none'
                            }
                        })
                        // Toggle current tooltip
                        tooltip.style.display = tooltip.style.display === 'none' ? 'flex' : 'none'
                    }
                }
            }
        })

        // Handle clicking outside to close tooltips
        document.addEventListener('click', (event) => {
            const target = event.target as HTMLElement
            if (!target.closest('.tooltip-container')) {
                this.querySelectorAll('.sector-tooltip').forEach(tooltip => {
                    if (tooltip instanceof HTMLElement) {
                        tooltip.style.display = 'none'
                    }
                })
            }
        })
    }
}